import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ShoppingBag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ProductCard } from '@/components/ProductCard';
import { getProducts, addToCart } from '@/lib/api';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import api from '@/lib/api';
import { PageSEO } from '@/components/SEO';
import { getImageUrl } from '@/lib/utils';

export default function Home() {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [siteSettings, setSiteSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);

  useEffect(() => {
    // Fetch site settings
    api.get('/site-settings')
      .then(res => setSiteSettings(res.data))
      .catch(err => console.error('Failed to load site settings:', err))
      .finally(() => setSettingsLoading(false));
    
    // Fetch featured products
    getProducts({ featured: true, limit: 6 })
      .then(res => setFeaturedProducts(res.data))
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  const handleAddToCart = async (product) => {
    try {
      await addToCart({ product_id: product.product_id, quantity: 1 });
      toast.success('Added to cart');
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Please login to add items to cart');
      } else {
        toast.error('Failed to add to cart');
      }
    }
  };

  // Default values if settings haven't loaded
  const hero = siteSettings?.hero || {};
  const heroImage = hero.background_image || '';
  const heroTitle = hero.title || 'Premium Shawls for Every Occasion';
  const heroSubtitle = hero.subtitle || 'Explore our curated collection of handpicked shawls crafted from the finest materials';
  const heroAccent = hero.accent_text || 'Discover Elegance';

  const collections = siteSettings?.collections || [];

  const cta = siteSettings?.cta || {};
  const ctaImage = cta.background_image || '';
  const ctaTitle = cta.title || 'New to Twenty Tulips?';
  const ctaDescription = cta.description || 'Sign up for exclusive access to new arrivals, special offers, and styling tips.';
  const ctaButtonText = cta.button_text || 'Create Account';

  return (
    <div className="min-h-screen" data-testid="home-page">
      <PageSEO.home />
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image - only show when loaded */}
        <div className="absolute inset-0">
          {settingsLoading ? (
            <div className="w-full h-full bg-stone-100 animate-pulse" />
          ) : heroImage ? (
            <img
              src={getImageUrl(heroImage)}
              alt="Hero"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-stone-100 to-stone-200" />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-white" />
        </div>

        {/* Hero Content */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className="relative z-10 text-center px-4 max-w-4xl"
        >
          <p className="font-accent text-2xl md:text-3xl text-stone-700 mb-4">
            {heroAccent}
          </p>
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-medium tracking-tight text-stone-900 mb-6 leading-[1.1]"
            data-testid="hero-title">
            {heroTitle}
          </h1>
          <p className="text-lg md:text-xl font-light text-stone-700 mb-12 max-w-2xl mx-auto leading-relaxed">
            {heroSubtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/shop">
              <Button className="btn-primary" size="lg" data-testid="shop-now-button">
                <ShoppingBag className="mr-2 h-5 w-5" />
                Shop Now
              </Button>
            </Link>
            <Link to="/collections">
              <Button variant="outline" className="btn-secondary" size="lg">
                View Collections
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </motion.div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
          <div className="animate-bounce">
            <div className="w-6 h-10 border-2 border-stone-400 rounded-full flex justify-center">
              <div className="w-1 h-3 bg-stone-400 rounded-full mt-2 animate-pulse" />
            </div>
          </div>
        </div>
      </section>

      {/* Collections Section */}
      <section className="py-24 md:py-32 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="collections-section">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-normal tracking-tight text-stone-900 mb-4">
            Our Collections
          </h2>
          <p className="text-base md:text-lg font-light text-stone-600 max-w-2xl mx-auto leading-relaxed">
            Each collection tells a unique story through fabric, color, and craftsmanship
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {settingsLoading ? (
            // Loading skeleton for collections
            [...Array(3)].map((_, i) => (
              <div key={i} className="aspect-[3/4] bg-stone-200 rounded-lg animate-pulse" />
            ))
          ) : collections.length > 0 ? (
            collections.map((collection, index) => (
              <motion.div
                key={collection.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Link
                  to={`/shop?collection=${collection.name}`}
                  className="group block relative overflow-hidden rounded-lg"
                  data-testid={`collection-${collection.name.toLowerCase()}`}
                >
                  <div className="aspect-[3/4] overflow-hidden">
                    {collection.image ? (
                      <img
                        src={getImageUrl(collection.image)}
                        alt={collection.name}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full bg-stone-200 flex items-center justify-center">
                        <span className="text-stone-400">{collection.name}</span>
                      </div>
                    )}
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                    <h3 className="text-2xl md:text-3xl font-normal mb-2">{collection.name}</h3>
                    <p className="text-sm text-white/90 mb-4">{collection.description}</p>
                    <span className="inline-flex items-center text-sm uppercase tracking-wide group-hover:translate-x-2 transition-transform">
                      Explore <ArrowRight className="ml-2 h-4 w-4" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))
          ) : null}
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-24 md:py-32 bg-stone-50" data-testid="featured-products-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-normal tracking-tight text-stone-900 mb-4">
              Best Sellers
            </h2>
            <p className="text-base md:text-lg font-light text-stone-600 max-w-2xl mx-auto leading-relaxed">
              Our most loved pieces, chosen by customers like you
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-12 gap-x-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="space-y-4">
                  <div className="aspect-[3/4] skeleton rounded-lg" />
                  <div className="h-4 skeleton rounded w-3/4" />
                  <div className="h-4 skeleton rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-12 gap-x-8">
              {featuredProducts.map((product) => (
                <ProductCard
                  key={product.product_id}
                  product={product}
                  onAddToCart={handleAddToCart}
                />
              ))}
            </div>
          )}

          <div className="text-center mt-16">
            <Link to="/shop">
              <Button className="btn-secondary" size="lg" data-testid="view-all-button">
                View All Products
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative rounded-2xl overflow-hidden">
          {settingsLoading ? (
            <div className="w-full h-[500px] bg-stone-200 animate-pulse" />
          ) : ctaImage ? (
            <img
              src={getImageUrl(ctaImage)}
              alt="CTA"
              className="w-full h-[500px] object-cover"
            />
          ) : (
            <div className="w-full h-[500px] bg-gradient-to-br from-stone-700 to-stone-900" />
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-transparent" />
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-xl px-12">
              <h2 className="text-3xl md:text-5xl font-normal text-white mb-6 leading-tight">
                {ctaTitle}
              </h2>
              <p className="text-lg text-white/90 mb-8 leading-relaxed">
                {ctaDescription}
              </p>
              <Link to="/login">
                <Button className="bg-white text-stone-900 hover:bg-stone-100 rounded-full px-8 py-6" size="lg">
                  {ctaButtonText}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
